'use client';
import { useQuery } from '@tanstack/react-query';

// Interfaces para los datos de Strapi Banner
interface StrapiImage {
  id: number;
  name: string;
  alternativeText?: string | null;
  caption?: string | null;
  width: number;
  height: number;
  formats?: any;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl?: string | null;
  provider: string;
  provider_metadata?: any;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

interface StrapiBannerItem {
  id: number;
  Activo: boolean;
  Boton: boolean;
  Url: string;
  Posicion_boton: string;
  Titulo?: string | null;
  Alineacion_titulo?: string | null;
  Posicion_titulo?: string | null;
  Fuente_titulo?: string | null;
  Descripcion?: string | null;
  Pieza_grafica?: StrapiImage;
  Objeto_fondo?: StrapiImage;
  Objeto_superior?: StrapiImage;
  Objeto_fondo_ajuste?: string | null;
  Objeto_fondo_posicion?: string | null;
  Objeto_superior_posicion?: string | null;
  Color_Fondo_Hex?: string | null;
  Color_Texto_Titulo_Hex?: string | null;
  Color_Texto_Descripcion_Hex?: string | null;
  Texto_boton?: string | null;
}

interface StrapiBannerData {
  id: number;
  documentId: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  Banner_Izquierda_Desktop: StrapiBannerItem[];
  Banner_Izquierda_Mobile: StrapiBannerItem[];
  Banner_Derecha_Desktop: StrapiBannerItem[];
  Banner_Derecha_Mobile: StrapiBannerItem[];
}

interface StrapiBannerResponse {
  data: StrapiBannerData;
  meta: any;
}

// Interfaces para los datos procesados
export interface ProcessedBannerItem {
  id: number;
  activo: boolean;
  boton: boolean;
  url: string;
  posicionBoton: string;
  titulo?: string | null;
  alineacionTitulo?: string | null;
  posicionTitulo?: string | null;
  fuente?: string | null;
  descripcion?: string | null;
  piezaGrafica?: {
    id: number;
    url: string;
    alternativeText?: string | null;
    width: number;
    height: number;
  };
  objetoFondo?: {
    id: number;
    url: string;
    alternativeText?: string | null;
    width: number;
    height: number;
  };
  objetoSuperior?: {
    id: number;
    url: string;
    alternativeText?: string | null;
    width: number;
    height: number;
  };
  objetoFondoAjuste?: string | null;
  objetoFondoPosicion?: string | null;
  objetoSuperiorPosicion?: string | null;
  colorFondoHex?: string | null;
  colorTitulo?: string | null;
  colorDescripcion?: string | null;
  textoBoton?: string | null;
}

export interface ProcessedBannerData {
  bannerIzquierdaDesktop: ProcessedBannerItem[];
  bannerIzquierdaMobile: ProcessedBannerItem[];
  bannerDerechaDesktop: ProcessedBannerItem[];
  bannerDerechaMobile: ProcessedBannerItem[];
}

interface UseStrapiBannerReturn {
  data: ProcessedBannerData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const fetchStrapiBannerData = async (): Promise<ProcessedBannerData> => {
  const response = await fetch('/api/strapi/banner', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    let errorMessage = `Error HTTP: ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch (jsonError) {
      console.error('Error parsing error response:', jsonError);
    }
    throw new Error(errorMessage);
  }

  let jsonData: StrapiBannerResponse;
  try {
    jsonData = await response.json();
  } catch (jsonError) {
    console.error('Error parsing Strapi response:', jsonError);
    throw new Error('Error al parsear respuesta de Strapi');
  }

  if (jsonData.data) {
    const bannerData = jsonData.data;
    
    // Función helper para procesar un banner individual
    const processBannerItem = (item: StrapiBannerItem): ProcessedBannerItem => ({
      id: item.id,
      activo: item.Activo,
      boton: item.Boton,
      url: item.Url,
      posicionBoton: item.Posicion_boton,
      titulo: item.Titulo,
      alineacionTitulo: item.Alineacion_titulo,
      posicionTitulo: item.Posicion_titulo,
      fuente: item.Fuente_titulo,
      descripcion: item.Descripcion,
      piezaGrafica: item.Pieza_grafica ? {
        id: item.Pieza_grafica.id,
        url: item.Pieza_grafica.url,
        alternativeText: item.Pieza_grafica.alternativeText,
        width: item.Pieza_grafica.width,
        height: item.Pieza_grafica.height
      } : undefined,
      objetoFondo: item.Objeto_fondo ? {
        id: item.Objeto_fondo.id,
        url: item.Objeto_fondo.url,
        alternativeText: item.Objeto_fondo.alternativeText,
        width: item.Objeto_fondo.width,
        height: item.Objeto_fondo.height
      } : undefined,
      objetoSuperior: item.Objeto_superior ? {
        id: item.Objeto_superior.id,
        url: item.Objeto_superior.url,
        alternativeText: item.Objeto_superior.alternativeText,
        width: item.Objeto_superior.width,
        height: item.Objeto_superior.height
      } : undefined,
      objetoFondoAjuste: item.Objeto_fondo_ajuste,
      objetoFondoPosicion: item.Objeto_fondo_posicion,
      objetoSuperiorPosicion: item.Objeto_superior_posicion,
      colorFondoHex: item.Color_Fondo_Hex,
      colorTitulo: item.Color_Texto_Titulo_Hex,
      colorDescripcion: item.Color_Texto_Descripcion_Hex,
      textoBoton: item.Texto_boton
    });
    
    // Procesar banners izquierdos de desktop
    const bannerIzquierdaDesktop: ProcessedBannerItem[] = (bannerData.Banner_Izquierda_Desktop || [])
      .filter(item => item.Activo)
      .map(processBannerItem);

    // Procesar banners izquierdos de mobile
    const bannerIzquierdaMobile: ProcessedBannerItem[] = (bannerData.Banner_Izquierda_Mobile || [])
      .filter(item => item.Activo)
      .map(processBannerItem);

    // Procesar banners derechos de desktop
    const bannerDerechaDesktop: ProcessedBannerItem[] = (bannerData.Banner_Derecha_Desktop || [])
      .filter(item => item.Activo)
      .map(processBannerItem);

    // Procesar banners derechos de mobile
    const bannerDerechaMobile: ProcessedBannerItem[] = (bannerData.Banner_Derecha_Mobile || [])
      .filter(item => item.Activo)
      .map(processBannerItem);

    return {
      bannerIzquierdaDesktop,
      bannerIzquierdaMobile,
      bannerDerechaDesktop,
      bannerDerechaMobile
    };
  } else {
    throw new Error('No se encontraron datos en la respuesta');
  }
};

const useStrapiBanner = (): UseStrapiBannerReturn => {
  const {
    data,
    isLoading: loading,
    error,
    refetch
  } = useQuery<ProcessedBannerData, Error>({
    queryKey: ['strapi-banner'],
    queryFn: fetchStrapiBannerData,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    data: data || null,
    loading,
    error: error?.message || null,
    refetch: () => refetch()
  };
};

export default useStrapiBanner;

